/**
 * alljourneys-catalog — the tenant's own services, locations and staff for the
 * All Journeys preview.
 *
 * The preview used to run on four baked fixture-tenant arrays, so every tenant saw
 * "Academic Advising" at "Fairmount Hall" and the "N services are live today"
 * hint counted a university nobody was looking at. This module reads the same
 * choke points the Services, Locations and Staff surfaces use and maps each row
 * into the shape the preview already speaks (id/name/desc/dur/locs, id/name/addr/tz,
 * id/name), so the preview describes the tenant.
 *
 * Every read is best-effort: a refusal or an empty catalog returns [] and the
 * caller keeps whatever it had. The preview is a picture of the journey, not a
 * report — it must never fail to draw because a list op was slow.
 */
import { listLocations, listStaff, listStudioServices, listQuestionGroups, listQuestions } from "@v0y4ge/api-client";
import { LOCATIONS_OPTS } from "../../shell/request-opts";
import { coordinatePairOf } from "./appointment-data";
import { publishedServicesOnly } from "./appointment-service-filters";

export interface PreviewService {
  id: string; name: string; desc: string; cat: string;
  dur: string; price: string; window: number; locs: string[];
  /** The service's persisted image, carried THROUGH from the catalogue row —
   *  never derived, never a placeholder. "" when the row has none, which is the
   *  signal for the consumer's own fallback.
   *
   *  Why it lives on the projection: AllJourneys keys its `SERVICE_IMAGES` map
   *  off the raw rows, and the admin/preview path had none to key off — so the
   *  preview drew no service images at all while the public read drew them.
   *  Carrying it here is what lets BOTH paths resolve the same persisted URL
   *  through the same helper. */
  image: string;
  /** Booking questions resolved by the read (seed review step 06); [] on an older worker. */
  questions: Record<string, unknown>[];
  /** The row's own creation instant, verbatim ("" when absent). Read only to
   *  pick the builder's default sample — the newest services. */
  createdAt: string;
  /** TRUE for a non-published row — only ever present in the builder's sample. */
  draft?: boolean;
}
export interface PreviewLocation {
  id: string; name: string; addr: string; dist: string; tz: string;
  next: string; hours: string; phone: string;
  /** Carried THROUGH from the tenant's location row, never derived. This is the
   *  projection that feeds the runtime `LOCATIONS` on the public and preview
   *  paths, so dropping the pair here left the journey's map with nothing to
   *  place even when the row had coordinates (build report §1, 2026-09-14).
   *  Optional: an unlocated place has none and must not be given any. */
  latitude?: number; longitude?: number;
}
export interface PreviewStaff { id: string; name: string; }
export interface PreviewDuration { id: string; label: string; sub: string; }

export interface PreviewCatalog {
  services: PreviewService[];
  /** Builder sample: the 3 newest services, drafts included. Absent on a failed read. */
  sampleServices?: PreviewService[];
  locations: PreviewLocation[];
  staff: PreviewStaff[];
  durations: PreviewDuration[];
}

const rows = (r: unknown, ...keys: string[]): Record<string, unknown>[] => {
  const o = (r || {}) as Record<string, unknown>;
  for (const k of keys) if (Array.isArray(o[k])) return o[k] as Record<string, unknown>[];
  return Array.isArray(r) ? (r as Record<string, unknown>[]) : [];
};
const str = (v: unknown, fallback = "") => (typeof v === "string" && v ? v : fallback);
const num = (v: unknown, fallback = 0) => (typeof v === "number" && isFinite(v) ? v : fallback);

/* ── Builder default data (2026-09-28, KP) ──
 * When a journey has nothing of its own to show, the builder and its preview
 * draw the tenant's most recently CREATED records instead of an empty page.
 * These are real rows from reads the designer already makes — nothing is
 * invented — and the builder labels them as sample data. Never used on the
 * customer page. */
export const SAMPLE_COUNT = 3;

/** A row's creation instant in ms, from `createdAt` / `created_at`; -Infinity when absent. */
export function createdTime(row: unknown): number {
  const r = (row || {}) as Record<string, unknown>;
  const t = Date.parse(str(r.createdAt) || str(r.created_at));
  return Number.isFinite(t) ? t : -Infinity;
}

/** The `n` newest rows by creation time, newest first. Rows with no timestamp sort last, in their original order. */
export function newestFirst<T>(rows: readonly T[], n: number = SAMPLE_COUNT, at: (row: T) => number = createdTime): T[] {
  return rows
    .map((row, i) => ({ row, i, t: at(row) }))
    .sort((a, b) => (b.t === a.t ? a.i - b.i : b.t - a.t))
    .slice(0, n)
    .map((x) => x.row);
}

/** A storable image address: http(s) or root-relative. Never `blob:` / `data:`.
 *
 *  Same rule as `durableServiceImageUrl` in screens/services/services-data.ts
 *  and `durableImageUrl` in AllJourneys.tsx. It is restated rather than imported
 *  ON PURPOSE: services-data hydrates the Services store on import (see the
 *  pager note below), and this module must stay free of that side effect. If
 *  the three ever need to become one, the shared home is a new side-effect-free
 *  module — not an import of services-data from here. */
const durableImageUrl = (value: unknown): string => {
  const v = typeof value === "string" ? value.trim() : "";
  return v && (/^https?:\/\//i.test(v) || /^\/(?!\/)/.test(v)) ? v : "";
};

/** The persisted image on a raw catalogue row: the row's own `image`, else its
 *  landing hero's. Exactly the precedence AllJourneys' `catalogServiceImages`
 *  applies to the public rows, so preview and live resolve the same address. */
function rowImage(s: Record<string, unknown>): string {
  const landing = s.landing as Record<string, unknown> | undefined;
  const hero = landing && typeof landing === "object" ? (landing.hero as Record<string, unknown> | undefined) : undefined;
  return durableImageUrl(s.image) || durableImageUrl(hero && hero.image);
}

/** "30 min" / "1 hr 30 min" — the label the preview's service rows print. */
function durLabel(mins: number): string {
  if (!mins) return "";
  const h = Math.floor(mins / 60), m = mins % 60;
  if (!h) return m + " min";
  return h + " hr" + (m ? " " + m + " min" : "");
}

/** Money the way a booking page shows it: the symbol when we know it, "Free" at zero. */
function priceLabel(major: number, currency: string): string {
  if (!major) return "Free";
  const sym = currency === "USD" ? "$" : currency === "GBP" ? "£" : currency === "EUR" ? "€" : "";
  const n = major.toFixed(2);
  return sym ? sym + n : n + " " + currency;
}

/** IANA zone → the six-zone vocabulary the preview's timezone control uses. */
function zoneKey(tz: string): string {
  const t = tz.toLowerCase();
  if (t.indexOf("anchorage") > -1) return "alaska";
  if (t.indexOf("honolulu") > -1) return "hawaii";
  if (t.indexOf("los_angeles") > -1 || t.indexOf("vancouver") > -1) return "pacific";
  if (t.indexOf("denver") > -1 || t.indexOf("phoenix") > -1) return "mountain";
  if (t.indexOf("chicago") > -1 || t.indexOf("winnipeg") > -1) return "central";
  return "eastern";
}

function addressOf(l: Record<string, unknown>): string {
  const direct = str(l.address) || str(l.fullAddress) || str(l.addressLine1);
  if (direct) {
    const city = str(l.city), post = str(l.postcode) || str(l.postalCode) || str(l.zip);
    return [direct, city, post].filter(Boolean).join(", ");
  }
  const a = l.address as Record<string, unknown> | undefined;
  if (a && typeof a === "object") {
    return [str(a.line1), str(a.city), str(a.postcode) || str(a.postalCode)].filter(Boolean).join(", ");
  }
  return "";
}

/* Every page of the admin service read the preview offers from. The route caps
 * a page at 100 rows whatever limit is asked, so a single `limit: 200` call
 * returned the first page only and a published service on a later page never
 * reached the preview. Pages on until the envelope reports no more; the loop
 * ceiling is sized from the page length the server says it applied (`limit`),
 * not the one requested. Throws when a promised page comes back empty, so the
 * best-effort catch in loadPreviewCatalog yields no services rather than a
 * short catalogue drawn as the whole. Local on purpose: the Services store's
 * pager lives in a module that hydrates on import. */
const PREVIEW_SERVICES_PAGE_LIMIT = 100;
export async function listAllPreviewServices(
  opts: Record<string, unknown>,
  list: (q: unknown, o: unknown) => Promise<unknown> = listStudioServices as (q: unknown, o: unknown) => Promise<unknown>,
): Promise<Record<string, unknown>[]> {
  const seen = new Map<string, Record<string, unknown>>();
  let page = 1;
  let maxPages = Infinity;
  for (;;) {
    const res = ((await list({ page: String(page), limit: String(PREVIEW_SERVICES_PAGE_LIMIT) }, opts)) || {}) as Record<string, unknown>;
    const pageRows = rows(res, "services", "items", "data");
    if (maxPages === Infinity) {
      const pageSize = num(res.limit) > 0 ? num(res.limit) : Math.max(pageRows.length, 1);
      maxPages = Math.ceil(num(res.total) / pageSize) + 1;
    }
    pageRows.forEach((row, i) => seen.set(str(row.serviceId) || str(row.id) || String.fromCharCode(0) + page + ":" + i, row));
    if (res.hasMore !== true) break;
    if (pageRows.length === 0 || page >= maxPages) {
      throw new Error("Preview services pagination incomplete: " + seen.size + " of " + num(res.total) + " after page " + page);
    }
    page += 1;
  }
  return Array.from(seen.values());
}

export function loadPreviewCatalog(signal?: AbortSignal): Promise<PreviewCatalog> {
  const nothing = { services: [], locations: [], staff: [], durations: [] };
  const svcP = typeof listStudioServices === "function"
    ? listAllPreviewServices({ ...LOCATIONS_OPTS, signal }).then((services) => ({ services })).catch(() => null)
    : Promise.resolve(null);
  const locP = typeof listLocations === "function"
    ? listLocations({ limit: 200 } as never, { ...LOCATIONS_OPTS, signal } as never).catch(() => null)
    : Promise.resolve(null);
  const staffP = typeof listStaff === "function"
    ? (listStaff as (q?: unknown) => Promise<unknown>)({ limit: 200 }).catch(() => null)
    : Promise.resolve(null);
  // The forms creator's groups and questions. StudioServiceDto keeps only the
  // group ids (`questions: string[]`) — its zod parse strips any resolved list —
  // so the resolution happens here, from the two reads the tab already serves.
  const groupsP = typeof listQuestionGroups === "function" ? (listQuestionGroups as (q?: unknown) => Promise<unknown>)({ limit: 200 }).catch(() => null) : Promise.resolve(null);
  const questionsP = typeof listQuestions === "function" ? (listQuestions as (q?: unknown) => Promise<unknown>)({ limit: 500 }).catch(() => null) : Promise.resolve(null);

  return Promise.all([svcP, locP, staffP, groupsP, questionsP]).then(([sr, lr, tr, gr, qr]) => {
    const groups = rows(gr, "data", "groups", "items");
    const questionById: Record<string, Record<string, unknown>> = {};
    rows(qr, "data", "questions", "items").forEach((q) => { const id = str(q.id); if (id) questionById[id] = q; });
    const resolveGroup = (groupId: string): Record<string, unknown>[] => {
      const g = groups.find((x) => str(x.id) === groupId);
      if (!g) return [];
      const items = Array.isArray(g.items) ? (g.items as Record<string, unknown>[]) : [];
      return items.map((it) => {
        const q = questionById[str(it.qid) || str(it.questionId)];
        if (!q || str(q.status) === "archived") return null;
        return { id: str(q.id), label: str(q.label), type: str(q.type), help: str(q.help), required: it.required === true || q.required === true, options: Array.isArray(q.options) ? q.options : [], groupId: str(g.id), groupName: str(g.name) };
      }).filter(Boolean) as Record<string, unknown>[];
    };
    const locations: PreviewLocation[] = rows(lr, "locations", "data", "items").map((l) => ({
      id: str(l.id) || str(l.locationId),
      name: str(l.name) || str(l.locationName, "Location"),
      addr: addressOf(l),
      dist: "",
      tz: zoneKey(str(l.timeZone) || str(l.timezone) || str(l.tz)),
      next: "",
      hours: str(l.openingHoursSummary) || str(l.hours),
      phone: str(l.phone) || str(l.telephone),
      // One complete pair, verbatim, when present (`latitude`/`longitude` or
      // `lat`/`lng`); absent when not — `0, 0` is a real position. Same rule as
      // the public projection, from the same helper, so the two cannot drift.
      ...coordinatePairOf(l),
    })).filter((l) => !!l.id);

    const allLocIds = locations.map((l) => l.id);
    // Only PUBLISHED services are offered on the journey. The Services admin
    // read returns every status -- that screen lists drafts on purpose -- so the
    // rule is applied here, once, before anything is derived from the rows:
    // categories, the duration step and the "N services" counts all read
    // SERVICES, and a draft that got this far would reach every one of them.
    const allServiceRows = rows(sr, "services", "items", "data").filter((s) => s.disabled !== true);
    const serviceRows = publishedServicesOnly(allServiceRows, "studio");
    const toPreview = (s: Record<string, unknown>): PreviewService => {
        const scope = Array.isArray(s.locScope) ? (s.locScope as unknown[]).map((x) => String(x)) : [];
        return {
          id: str(s.serviceId) || str(s.id),
          name: str(s.name, "Service"),
          desc: str(s.description),
          // The catalogue's category is a machine key (the intent id); its
          // human name rides alongside as `categoryName`. The label is what the
          // service step groups by and prints on its filter chips, so prefer it
          // and fall back to the key only when the row carries no name.
          cat: str(s.categoryName) || str(s.category, "general"),
          dur: str(s.listedDuration) || durLabel(num(s.durationMinutes) || num(s.duration)),
          price: priceLabel(num(s.price), str(s.currency, "USD")),
          // The catalog has no per-service booking window; the preview's own
          // default stands rather than a number invented here.
          window: 60,
          locs: scope.length ? scope : allLocIds,
          // The persisted image, verbatim from the row — "" when it has none, so
          // the consumer falls back rather than being handed a placeholder.
          image: rowImage(s),
          // The appointments group is the first id the DTO lists (engine order: appointments · queueing · events).
          questions: Array.isArray(s.bookingQuestions) && (s.bookingQuestions as unknown[]).length
            ? (s.bookingQuestions as Record<string, unknown>[])
            : resolveGroup(Array.isArray(s.questions) && typeof (s.questions as unknown[])[0] === "string" ? String((s.questions as unknown[])[0]) : ""),
          createdAt: str(s.createdAt) || str(s.created_at),
          draft: str(s.status).toLowerCase() !== "published",
        };
      };
    const services: PreviewService[] = serviceRows.map(toPreview).filter((s) => !!s.id);
    /* Builder sample (2026-09-28, KP — option 2): the tenant's 3 newest services
       INCLUDING drafts, flagged `draft`. Builder/preview only; never offered on
       the customer page, which reads `services` (published only) or the public read. */
    const sampleServices: PreviewService[] = newestFirst(allServiceRows.map(toPreview).filter((s) => !!s.id), SAMPLE_COUNT);

    const staff: PreviewStaff[] = rows(tr, "data", "staff", "members", "items").map((m) => ({
      id: str(m.id) || str(m.staffId),
      name: str(m.name) || [str(m.firstName), str(m.lastName)].filter(Boolean).join(" ") || "Team member",
    })).filter((m) => !!m.id);

    // A multi-duration service offers the catalog's own distinct lengths, so the
    // duration step shows what this tenant actually sells.
    const mins = Array.from(new Set((serviceRows.length ? serviceRows : allServiceRows)
      .map((s) => num(s.durationMinutes) || num(s.duration))
      .filter((m) => m > 0))).sort((a, b) => a - b).slice(0, 4);
    const durations: PreviewDuration[] = mins.map((m, i) => ({
      id: "d" + (i + 1), label: durLabel(m), sub: "",
    }));

    return { services, sampleServices, locations, staff, durations };
  }).catch(() => nothing);
}
